/**
 * client/src/features/income/utils/incomeUtils.js
 * 
 * Utility functions for income formatting, calculations, and conversions.
 */

import { cleanIncomeName, ensureString, safeReplace } from '../../../utils/stringUtils';

/**
 * Format currency amount with dollar sign and commas
 * @param {number} amount - The amount to format
 * @param {boolean} showCents - Whether to display cents
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCents = false) => {
  if (amount === null || amount === undefined) return '$0';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });
  
  return formatter.format(amount);
};

/**
 * Format percentage with % sign
 * @param {number} value - The percentage value (0-100)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${Math.round(value)}%`;
};

/**
 * Calculate annual amount from monthly amount
 * @param {number} monthlyAmount - The monthly amount
 * @returns {number} Annual amount
 */
export const calculateAnnualFromMonthly = (monthlyAmount) => {
  return (monthlyAmount || 0) * 12;
};

/**
 * Calculate monthly amount based on frequency and amount
 * @param {number} amount - The income amount
 * @param {string} frequency - The frequency (WEEKLY, BI_WEEKLY, MONTHLY, etc.)
 * @returns {number} Calculated monthly amount
 */
export const calculateMonthlyAmount = (amount, frequency) => {
  if (!amount) return 0;
  
  const amountValue = parseFloat(amount);
  if (isNaN(amountValue)) return 0;
  const rawFreq = (frequency || '').toString();
  const freq = rawFreq.toUpperCase().replace(/-/g, '_');
  
  // 🔍 DEBUG: Log frequency conversion
  console.log(`🔍 [calculateMonthlyAmount] Converting:`, {
    amount: amountValue,
    rawFrequency: rawFreq,
    normalizedFrequency: freq
  });
  
  let result = 0;
  switch (freq) {
    case 'WEEKLY':
      result = amountValue * 4.33; // Average weeks in a month
      break;
    case 'BI_WEEKLY':
      result = amountValue * 2.17; // Average bi-weeks in a month
      break;
    case 'BIWEEKLY':
      result = amountValue * 2.17;
      break;
    case 'MONTHLY':
      result = amountValue;
      break;
    case 'QUARTERLY':
      result = amountValue / 3;
      break;
    case 'ANNUALLY':
      result = amountValue / 12;
      break;
    case 'YEARLY':
      result = amountValue / 12;
      break;
    case 'ONE_TIME':
      result = 0; // One-time income isn't counted in monthly calculations
      break;
    default:
      console.warn(`⚠️ [calculateMonthlyAmount] Unknown frequency "${freq}" (raw: "${rawFreq}") - using amount as-is`);
      result = amountValue;
  }
  
  console.log(`🔍 [calculateMonthlyAmount] Result:`, {
    input: amountValue,
    frequency: freq,
    output: result,
    formula: freq === 'BIWEEKLY' || freq === 'BI_WEEKLY' ? `${amountValue} * 2.17` : 'other'
  });
  
  return result;
};

/**
 * Format date string to readable format
 * @param {string} dateString - The date string
 * @param {string} format - Format type ('short', 'medium', 'long')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'medium') => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  const options = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' }
  };
  
  return date.toLocaleDateString('en-US', options[format] || options.medium);
};

/**
 * Get human-readable frequency label
 * @param {string} frequency - The frequency code
 * @returns {string} Human-readable frequency label
 */
export const getFrequencyLabel = (frequency) => {
  if (!frequency) return 'Unknown';
  
  const freq = frequency.toString().toUpperCase().replace(/-/g, '_');
  const labels = {
    'WEEKLY': 'Weekly',
    'BI_WEEKLY': 'Bi-Weekly',
    'BIWEEKLY': 'Bi-Weekly',
    'MONTHLY': 'Monthly',
    'QUARTERLY': 'Quarterly',
    'ANNUALLY': 'Annually',
    'YEARLY': 'Annually',
    'ONE_TIME': 'One-Time'
  };
  
  return labels[freq] || freq;
};

/**
 * Get appropriate icon for income type
 * @param {string} type - The income type
 * @returns {JSX.Element} Icon component
 */
export const getIncomeTypeIcon = (type) => {
  // Implementation details...
  return '💰'; // Placeholder
};

/**
 * Calculate total monthly income from an array of incomes
 * @param {Array} incomes - Array of income objects
 * @returns {Object} Object with gross, net, and tax totals
 */
export const calculateTotalIncome = (incomes = []) => {
  if (!incomes || !incomes.length) {
    return { gross: 0, net: 0, tax: 0 };
  }
  
  let totalGross = 0;
  let totalNet = 0;
  let totalTax = 0;
  
  incomes.forEach(income => {
    const grossAmount = income.grossAmount || income.amount || 0;
    
    // For monthly frequency, use amounts directly
    const monthlyGross = income.frequency === 'MONTHLY'
      ? grossAmount
      : calculateMonthlyAmount(grossAmount, income.frequency);
    
    // Calculate tax from withholdings
    const taxes = income.taxes || {};
    const totalWithholdings = (
      (taxes.federal?.withholding || 0) +    // Fed Withholding
      (taxes.federal?.medicare || 0) +        // Fed MED/EE
      (taxes.federal?.socialSecurity || 0) +  // Fed OASDI/EE
      (taxes.state?.withholding || 0)        // State Withholding
    );
    
    // For monthly frequency, use tax amount directly
    const monthlyTax = income.frequency === 'MONTHLY'
      ? totalWithholdings
      : calculateMonthlyAmount(totalWithholdings, income.frequency);
    
    // Calculate monthly net
    const monthlyNet = monthlyGross - monthlyTax;
    
    totalGross += monthlyGross;
    totalNet += monthlyNet;
    totalTax += monthlyTax;
  });
  
  return {
    gross: totalGross,
    net: totalNet,
    tax: totalTax
  };
};

/**
 * Calculate total income for a specific bucket
 * @param {Array} incomes - Array of income objects
 * @param {string} bucketId - ID of the bucket
 * @returns {Object} Object with gross, net, and tax totals for the bucket
 */
export const calculateBucketIncome = (incomes = [], bucketId) => {
  if (!incomes || !incomes.length) {
    return { gross: 0, net: 0, tax: 0 };
  }

  // Filter incomes for this bucket
  const bucketIncomes = incomes
    .filter(income => (
      isSameObjectId(income.bucketId, bucketId) || 
      isSameObjectId(income.bucket, bucketId)
    ))
    // Important: wrap predicate to avoid filter passing index/array into isIncomeActive
    .filter(inc => isIncomeActive(inc));
  
  if (!bucketIncomes.length) {
    return { gross: 0, net: 0, tax: 0 };
  }

  const totals = bucketIncomes.reduce((acc, income) => {
    // Get the gross amount
    const grossAmount = income.typeSpecificDetails?.salary?.payDetails?.grossAmount || 
                       income.typeSpecificDetails?.contractor?.payDetails?.grossAmount ||
                       income.grossAmount || 
                       income.amount || 
                       0;

    // Get the frequency
    const frequency = (income.typeSpecificDetails?.salary?.payDetails?.frequency || 
                      income.typeSpecificDetails?.contractor?.payDetails?.frequency ||
                      income.frequency || 
                      'MONTHLY').toUpperCase();

    // Calculate monthly gross
    const monthlyGross = frequency === 'MONTHLY' 
      ? grossAmount
      : calculateMonthlyAmount(grossAmount, frequency);

    // Calculate monthly net based on income type
    let monthlyNet = 0;
    
    if (income.type === 'SALARY' || income.type === 'W2') {
      // For salary, use the net amount directly if available
      const netAmount = income.typeSpecificDetails?.salary?.payDetails?.netAmount ||
                       income.netAmount ||
                       income.net ||
                       (grossAmount - (income.taxes?.total || 0));
                       
      monthlyNet = frequency === 'MONTHLY'
        ? netAmount
        : calculateMonthlyAmount(netAmount, frequency);
    } else if (income.type === 'CONTRACTOR' || income.type === 'CONTRACT') {
      // For contractors, first check if we have an annual netIncome value
      const contractorTaxes = income.typeSpecificDetails?.contractor?.taxes || income.taxes || {};
      
      // Check for annual netIncome field (contractor data is often stored annually)
      if (contractorTaxes.netIncome) {
        // netIncome is annual, divide by 12 to get monthly
        monthlyNet = contractorTaxes.netIncome / 12;
      } else {
        // Fallback: calculate net from gross minus individual tax breakdowns
        const totalTaxes = (
          (contractorTaxes.federal || 0) +
          (contractorTaxes.state || 0) +
          (contractorTaxes.medicare || 0) +
          (contractorTaxes.socialSecurity || 0) +
          (contractorTaxes.local || 0)
        );
        
        const netAmount = grossAmount - totalTaxes;
        monthlyNet = frequency === 'MONTHLY'
          ? netAmount
          : calculateMonthlyAmount(netAmount, frequency);
      }
    } else {
      // For other types, estimate net as gross minus 23% tax if no net amount provided
      const netAmount = income.netAmount || income.net || (grossAmount * 0.77);
      monthlyNet = frequency === 'MONTHLY'
        ? netAmount
        : calculateMonthlyAmount(netAmount, frequency);
    }

    // Calculate monthly tax as the difference
    const monthlyTax = monthlyGross - monthlyNet;

    // Debug log for development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Income Calculation for ${income.name || 'unnamed income'} (${income._id}):`, {
        type: income.type,
        grossAmount,
        monthlyGross,
        monthlyNet,
        monthlyTax,
        frequency,
        rawIncome: income
      });
    }

    return {
      gross: acc.gross + monthlyGross,
      net: acc.net + monthlyNet,
      tax: acc.tax + monthlyTax
    };
  }, { gross: 0, net: 0, tax: 0 });

  // Round the totals to 2 decimal places for consistency
  return {
    gross: Math.round(totals.gross * 100) / 100,
    net: Math.round(totals.net * 100) / 100,
    tax: Math.round(totals.tax * 100) / 100
  };
};

/**
 * Determine if an income is active at the current time
 */
export const isIncomeActive = (income, at = new Date()) => {
  if (!income) return false;
  if (income.isActive === false) return false;
  const start = income.startDate ? new Date(income.startDate) : null;
  const end = income.endDate ? new Date(income.endDate) : null;
  const started = !start || start <= at;
  const notEnded = !end || end >= at;
  return started && notEnded;
};

/**
 * Normalize a MongoDB ObjectId reference to a consistent string format
 * @param {string|Object} id - The MongoDB ObjectId (string, object with _id, or direct ObjectId)
 * @returns {string|null} Normalized string representation or null if invalid
 */
export const normalizeObjectId = (id) => {
  if (!id) return null;
  
  // If it's a string, return it directly
  if (typeof id === 'string') return id;
  
  // If it's an object with _id property
  if (typeof id === 'object' && id._id) {
    return typeof id._id === 'string' ? id._id : String(id._id);
  }
  
  // If it's an object that can be stringified (like MongoDB ObjectId)
  if (typeof id === 'object') {
    return String(id);
  }
  
  return null;
};

/**
 * Check if two IDs represent the same MongoDB ObjectId
 * @param {string|Object} id1 - First ID to compare
 * @param {string|Object} id2 - Second ID to compare
 * @returns {boolean} True if IDs represent the same ObjectId
 */
export const isSameObjectId = (id1, id2) => {
  const normalized1 = normalizeObjectId(id1);
  const normalized2 = normalizeObjectId(id2);
  
  if (!normalized1 || !normalized2) return false;
  
  return normalized1 === normalized2;
};

/**
 * Debug utility for inspecting income-bucket relationships
 * @param {Array} incomes - Income objects
 * @param {Array} buckets - Bucket objects
 */
export const debugIncomeObjects = (incomes, buckets) => {
  if (!incomes || !buckets) {
    console.log('❌ Missing incomes or buckets for debugging');
    return;
  }
  
  console.group('🔍 Income-Bucket Relationships');
  
  incomes.forEach((income, index) => {
    const bucketId = income.bucketId || (income.bucket && normalizeObjectId(income.bucket));
    const matchingBucket = buckets.find(bucket => isSameObjectId(bucket._id, bucketId));
    
    console.log(`Income ${index + 1}: ${income.name || 'Unnamed'}`);
    console.log(`- ID: ${income._id}`);
    console.log(`- Bucket reference: ${bucketId || 'None'}`);
    console.log(`- Matched bucket: ${matchingBucket ? matchingBucket.name : 'None'}`);
  });
  
  console.groupEnd();
};

/**
 * Normalize income data from different sources into a consistent structure
 * @param {Object} income - Income object
 * @returns {Object} Normalized income data with consistent field access
 */
export const normalizeIncomeData = (income) => {
  if (!income) {
    console.warn('⚠️ normalizeIncomeData called with null/undefined income');
    return {
      _id: null,
      name: 'Unknown Income',
      grossAmount: 0,
      netAmount: 0,
      frequency: 'MONTHLY',
      taxes: {},
      type: 'UNKNOWN',
      startDate: new Date(),
      endDate: null,
      deductions: {},
      location: {},
      description: '',
      source: 'Unknown'
    };
  }
  
  // Log input data for debugging
  console.log('🔄 normalizeIncomeData input:', {
    id: income._id,
    name: income.name,
    type: income.type,
    category: income.category,
    rawType: (income.type || income.category || '').toUpperCase(),
    hasTypeSpecific: !!income.typeSpecificDetails
  });
  
  // Try to normalize the income type from multiple possible sources
  let incomeType = 'OTHER';
  
  // First check for type in standardized properties
  if (income.type) {
    incomeType = income.type.toUpperCase();
    console.log(`🔍 Found income type: ${incomeType} from income.type`);
  } else if (income.category) {
    incomeType = income.category.toUpperCase();
    console.log(`🔍 Found income type: ${incomeType} from income.category`);
  }
  
  // Check for employment or salary in category or type - normalize to SALARY
  if (['EMPLOYMENT', 'W2', 'SALARY'].includes(incomeType)) {
    incomeType = 'SALARY';
    console.log('🔄 Normalizing employment type to SALARY for consistency');
  }
  
  // Special handling for CONTRACT and CONTRACTOR types - standardize to CONTRACTOR
  if (incomeType === 'CONTRACT') {
    incomeType = 'CONTRACTOR';
    console.log('🔄 Normalizing CONTRACT type to CONTRACTOR for consistency');
  }
  
  // Check for type in typeSpecificDetails
  if (income.typeSpecificDetails) {
    // Type-specific checks
    if (income.typeSpecificDetails.salary) {
      incomeType = 'SALARY';
      console.log('🔍 Found SALARY type from typeSpecificDetails.salary');
    } else if (income.typeSpecificDetails.contractor || 
              income.typeSpecificDetails.client || 
              income.typeSpecificDetails.clientInfo) {
      incomeType = 'CONTRACTOR';
      console.log('🔍 Found CONTRACTOR type from typeSpecificDetails');
    }
  }
  
  // Additional check for employer information which suggests salary
  if (income.employerName && incomeType === 'OTHER') {
    incomeType = 'SALARY';
    console.log('🔍 Inferring SALARY type based on presence of employerName');
  }
  
  // Presence of W4 information indicates salary
  if (income.w4 && Object.keys(income.w4).length > 0 && incomeType === 'OTHER') {
    incomeType = 'SALARY';
    console.log('🔍 Inferring SALARY type based on presence of W4 information');
  }
  
  console.log(`✅ Final determined income type: ${incomeType}`);
  
  // Normalize frequency and synonyms to uppercase codes for consistency
  const mapFreq = (f) => {
    if (!f) return 'MONTHLY';
    const up = f.toString().toUpperCase();
    if (up === 'YEARLY') return 'ANNUALLY';
    if (up === 'BIWEEKLY') return 'BI_WEEKLY';
    if (up === 'SEMI-MONTHLY' || up === 'SEMIMONTHLY') return 'MONTHLY';
    return up;
  };
  const frequency = mapFreq(income.typeSpecificDetails?.salary?.payDetails?.frequency || income.frequency);
  
  // Get amounts from either root level or type-specific nested structure
  // Prioritize 'amount' field which is the primary field updated during saves
  const grossAmount = 
    income.amount ||
    income.typeSpecificDetails?.salary?.payDetails?.grossAmount || 
    income.typeSpecificDetails?.contractor?.payDetails?.grossAmount ||
    income.grossAmount ||
    0;
    
  const netAmount = 
    income.typeSpecificDetails?.salary?.payDetails?.netAmount || 
    income.typeSpecificDetails?.contractor?.payDetails?.netAmount ||
    income.netAmount || 
    0;
    
  // Get taxes from either root level or type-specific nested structure
  const taxes = 
    income.typeSpecificDetails?.salary?.taxWithholdings || 
    income.typeSpecificDetails?.contractor?.taxSummary ||
    income.taxes || 
    {};
    
  // Basic fields common to all income types
  const baseIncome = {
    _id: income._id || null,
    name: cleanIncomeName(income.name) || 'Unnamed Income',
    grossAmount,
    netAmount,
    frequency,
    taxes,
    type: incomeType,
    startDate: income.startDate ? new Date(income.startDate) : new Date(),
    endDate: income.endDate ? new Date(income.endDate) : null,
    bucketId: income.bucketId,
    bucket: income.bucket,
    description: income.description || income.notes || '',
    source: income.source || ''
  };
  
  // Type-specific normalizations
  let normalizedIncome = { ...baseIncome };
  
  // Type-specific handling
  switch (incomeType) {
    case 'SALARY':
      normalizedIncome = {
        ...normalizedIncome,
        employer: income.typeSpecificDetails?.salary?.employer?.name || 
                 income.typeSpecificDetails?.employer?.name || 
                 income.employerName ||
                 'Not specified',
        location: {
          state: income.typeSpecificDetails?.salary?.employer?.location?.state || 
                 income.typeSpecificDetails?.employer?.location?.state || 
                 income.location?.state ||
                 'Not specified'
        },
        w4: income.typeSpecificDetails?.salary?.w4 ? {
          filingStatus: income.typeSpecificDetails.salary.w4.filingStatus || 'SINGLE',
          dependents: income.typeSpecificDetails.salary.w4.dependents || 0,
          otherIncome: income.typeSpecificDetails.salary.w4.otherIncome || 0,
          deductions: income.typeSpecificDetails.salary.w4.deductions || 0,
          extraWithholding: income.typeSpecificDetails.salary.w4.extraWithholding || 0
        } : (income.w4 ? {
          filingStatus: income.w4.filingStatus || 'SINGLE',
          dependents: income.w4.dependents || 0,
          otherIncome: income.w4.otherIncome || 0,
          deductions: income.w4.deductions || 0,
          extraWithholding: income.w4.extraWithholding || 0
        } : {}),
        deductions: income.typeSpecificDetails?.salary?.deductions ? {
          retirement: income.typeSpecificDetails.salary.deductions.retirement || 0,
          health: income.typeSpecificDetails.salary.deductions.health || 0,
          dental: income.typeSpecificDetails.salary.deductions.dental || 0,
          vision: income.typeSpecificDetails.salary.deductions.vision || 0,
          other: income.typeSpecificDetails.salary.deductions.other || 0
        } : (income.deductions ? {
          retirement: income.deductions.retirement || 0,
          health: income.deductions.health || 0,
          dental: income.deductions.dental || 0,
          vision: income.deductions.vision || 0,
          other: income.deductions.other || 0
        } : {})
      };
      break;
      
    case 'CONTRACTOR':
      const contractorDetails = income.typeSpecificDetails?.contractor || {};
      normalizedIncome = {
        ...normalizedIncome,
        employer: contractorDetails.employer || 
                 income.client || 
                 income.employerName || 
                 'Not specified',
        businessStructure: contractorDetails.businessStructure || 'SOLE_PROPRIETOR',
        location: {
          state: contractorDetails.location?.state || 
                 income.location?.state || 
                 'Not specified'
        },
        filingStatus: contractorDetails.filingStatus || 'SINGLE',
        typeSpecificDetails: {
          contractor: {
            ...contractorDetails,
            taxes: contractorDetails.taxes || {},
            expenses: contractorDetails.expenses || {}
          }
        }
      };
      break;
      
    case 'RENTAL':
      normalizedIncome = {
        ...normalizedIncome,
        location: {
          propertyAddress: income.typeSpecificDetails?.rental?.propertyAddress || 
                          income.propertyAddress || 
                          'Not specified',
          propertyType: income.typeSpecificDetails?.rental?.propertyType || 'RESIDENTIAL',
          state: income.typeSpecificDetails?.rental?.state || 
                income.location?.state || 
                'Not specified'
        },
        deductions: {
          rental: {
            monthlyExpenses: income.typeSpecificDetails?.rental?.monthlyExpenses || 
                            income.expenses?.monthlyExpenses || 
                            0,
            mortgagePayment: income.typeSpecificDetails?.rental?.mortgagePayment || 
                            income.expenses?.mortgagePayment || 
                            0,
            propertyTax: income.typeSpecificDetails?.rental?.propertyTax || 
                        income.expenses?.propertyTax || 
                        0,
            insurance: income.typeSpecificDetails?.rental?.insurance || 
                      income.expenses?.insurance || 
                      0,
            maintenance: income.typeSpecificDetails?.rental?.maintenance || 
                        income.expenses?.maintenance || 
                        0,
            utilities: income.typeSpecificDetails?.rental?.utilities || 
                      income.expenses?.utilities || 
                      0
          }
        },
        taxable: income.typeSpecificDetails?.rental?.taxable !== undefined ? 
                income.typeSpecificDetails.rental.taxable : 
                (income.taxable !== undefined ? income.taxable : true)
      };
      break;
      
    case 'INVESTMENT':
      normalizedIncome = {
        ...normalizedIncome,
        typeSpecificDetails: {
          investment: {
            investmentType: income.typeSpecificDetails?.investment?.investmentType || 
                           income.investmentType || 
                           'NOT_SPECIFIED',
            institution: income.typeSpecificDetails?.investment?.institution || 
                        income.institution || 
                        'Not specified',
            accountType: income.typeSpecificDetails?.investment?.accountType || 
                        income.accountType || 
                        'Not specified',
            yieldRate: income.typeSpecificDetails?.investment?.yieldRate || 
                      income.yieldRate || 
                      0
          }
        },
        taxable: income.typeSpecificDetails?.investment?.taxable !== undefined ? 
                income.typeSpecificDetails.investment.taxable : 
                (income.taxable !== undefined ? income.taxable : true),
        taxRate: income.typeSpecificDetails?.investment?.taxRate || 
                income.taxRate || 
                15 // Default capital gains rate
      };
      break;
      
    case 'BENEFIT':
      normalizedIncome = {
        ...normalizedIncome,
        typeSpecificDetails: {
          benefit: {
            benefitType: income.typeSpecificDetails?.benefit?.benefitType || 
                        income.benefitType || 
                        'NOT_SPECIFIED',
            provider: income.typeSpecificDetails?.benefit?.provider || 
                     income.provider || 
                     'Not specified',
            caseNumber: income.typeSpecificDetails?.benefit?.caseNumber || 
                       income.caseNumber || 
                       'Not specified'
          }
        },
        taxable: income.typeSpecificDetails?.benefit?.taxable !== undefined ? 
                income.typeSpecificDetails.benefit.taxable : 
                (income.taxable !== undefined ? income.taxable : false) // Most benefits non-taxable by default
      };
      break;
      
    default:
      // Handle other income types
      normalizedIncome = {
        ...normalizedIncome,
        taxable: income.taxable !== undefined ? income.taxable : true,
        location: {
          state: income.location?.state || 'Not specified'
        }
      };
  }
    
  // Log normalized result for debugging
  console.log('🔄 normalizeIncomeData result:', {
    id: normalizedIncome._id,
    type: normalizedIncome.type,
    grossAmount: normalizedIncome.grossAmount,
    netAmount: normalizedIncome.netAmount
  });
  
  return normalizedIncome;
};

/**
 * Parse currency string back to number
 * @param {string} value - The currency string to parse (e.g. "$1,234.56")
 * @returns {number} Parsed number value
 */
export const parseCurrency = (value) => {
  if (!value) return '';
  // Remove currency symbol, commas and other non-numeric characters except decimal point
  const cleanValue = safeReplace(value, /[^0-9.]/g, '');
  const number = parseFloat(cleanValue);
  return isNaN(number) ? '' : number;
};

/**
 * Calculate total income across ALL incomes (regardless of bucket)
 * Uses the same calculation logic as calculateBucketIncome for consistency
 * 
 * This function is used by the Cash Flow Summary page to ensure the Monthly Net Income
 * matches the value shown on the Income page.
 * 
 * @param {Array} incomes - Array of income objects
 * @returns {Object} Object with gross, net, and tax totals
 */
export const calculateAllIncomeTotals = (incomes = []) => {
  if (!incomes || !incomes.length) {
    return { gross: 0, net: 0, tax: 0 };
  }

  // Filter to only active incomes
  const activeIncomes = incomes.filter(inc => isIncomeActive(inc));

  if (!activeIncomes.length) {
    return { gross: 0, net: 0, tax: 0 };
  }

  const totals = activeIncomes.reduce((acc, income) => {
    // Get the gross amount - same logic as calculateBucketIncome
    const grossAmount = income.typeSpecificDetails?.salary?.payDetails?.grossAmount || 
                       income.typeSpecificDetails?.contractor?.payDetails?.grossAmount ||
                       income.grossAmount || 
                       income.amount || 
                       0;

    // Get the frequency - same logic as calculateBucketIncome
    const rawFrequency = income.typeSpecificDetails?.salary?.payDetails?.frequency || 
                      income.typeSpecificDetails?.contractor?.payDetails?.frequency ||
                      income.frequency || 
                      'MONTHLY';
    const frequency = rawFrequency.toString().toUpperCase().replace(/-/g, '_');

    // 🔍 DEBUG: Log frequency normalization
    console.log(`🔍 [calculateAllIncomeTotals] Income "${income.name || 'unnamed'}":`, {
      rawFrequency,
      normalizedFrequency: frequency,
      grossAmount,
      netAmount: income.netAmount || income.typeSpecificDetails?.salary?.payDetails?.netAmount
    });

    // Calculate monthly gross
    let monthlyGross = frequency === 'MONTHLY' 
      ? grossAmount
      : calculateMonthlyAmount(grossAmount, rawFrequency);
    
    console.log(`🔍 [calculateAllIncomeTotals] Monthly Gross calculation:`, {
      grossAmount,
      frequency,
      monthlyGross,
      calculation: frequency === 'MONTHLY' ? 'direct' : `calculateMonthlyAmount(${grossAmount}, "${rawFrequency}")`
    });

    // Calculate monthly net based on income type - same logic as calculateBucketIncome
    let monthlyNet = 0;
    
    if (income.type === 'SALARY' || income.type === 'W2') {
      // For salary, use the net amount directly if available
      const netAmount = income.typeSpecificDetails?.salary?.payDetails?.netAmount ||
                       income.netAmount ||
                       income.net ||
                       (grossAmount - (income.taxes?.total || 0));
      
      console.log(`🔍 [calculateAllIncomeTotals] Salary Net calculation:`, {
        netAmount,
        frequency,
        rawFrequency,
        fromPayDetails: income.typeSpecificDetails?.salary?.payDetails?.netAmount,
        fromRoot: income.netAmount,
        calculated: grossAmount - (income.taxes?.total || 0)
      });
                       
      monthlyNet = frequency === 'MONTHLY'
        ? netAmount
        : calculateMonthlyAmount(netAmount, rawFrequency);
      
      console.log(`🔍 [calculateAllIncomeTotals] Monthly Net calculation:`, {
        netAmount,
        frequency,
        monthlyNet,
        calculation: frequency === 'MONTHLY' ? 'direct' : `calculateMonthlyAmount(${netAmount}, "${rawFrequency}")`
      });
    } else if (income.type === 'CONTRACTOR' || income.type === 'CONTRACT' || income.category === 'CONTRACT' || income.category === 'CONTRACTOR') {
      // For contractors, first check if we have an annual netIncome value
      const contractorTaxes = income.typeSpecificDetails?.contractor?.taxes || income.taxes || {};
      
      // Check for annual netIncome field (contractor data is often stored annually)
      if (contractorTaxes.netIncome) {
        // netIncome is annual, divide by 12 to get monthly
        monthlyNet = contractorTaxes.netIncome / 12;
        console.log(`📊 Contractor ${income.name}: Using annual netIncome ${contractorTaxes.netIncome} / 12 = ${monthlyNet}`);
      } else {
        // Fallback: calculate net from gross minus individual tax breakdowns
        const totalTaxes = (
          (contractorTaxes.federal || 0) +
          (contractorTaxes.state || 0) +
          (contractorTaxes.medicare || 0) +
          (contractorTaxes.socialSecurity || 0) +
          (contractorTaxes.local || 0)
        );
        
        const netAmount = grossAmount - totalTaxes;
        monthlyNet = frequency === 'MONTHLY'
          ? netAmount
          : calculateMonthlyAmount(netAmount, frequency);
      }
    } else {
      // For other types, estimate net as gross minus 23% tax if no net amount provided
      const netAmount = income.netAmount || income.net || (grossAmount * 0.77);
      monthlyNet = frequency === 'MONTHLY'
        ? netAmount
        : calculateMonthlyAmount(netAmount, frequency);
    }

    // 🔍 VALIDATION: Ensure monthlyGross >= monthlyNet for this income
    // If not, there's a data issue - correct it
    if (monthlyNet > monthlyGross && monthlyGross > 0) {
      console.warn(`⚠️ [calculateAllIncomeTotals] Income "${income.name || 'unnamed'}" has Net > Gross:`, {
        grossAmount,
        monthlyGross,
        monthlyNet,
        frequency,
        type: income.type,
        category: income.category
      });
      
      // If net is way higher, it might be that net is actually the gross
      if (monthlyNet > monthlyGross * 1.2) {
        console.warn(`⚠️ [calculateAllIncomeTotals] Net is >20% higher - treating Net as Gross for "${income.name || 'unnamed'}"`);
        // Swap: use net as gross, recalculate net
        const correctedGross = monthlyNet;
        monthlyNet = correctedGross * 0.77; // Estimate 23% tax
        return {
          gross: acc.gross + correctedGross,
          net: acc.net + monthlyNet,
          tax: acc.tax + (correctedGross - monthlyNet)
        };
      } else {
        // Just swap them
        const temp = monthlyGross;
        monthlyGross = monthlyNet;
        monthlyNet = temp;
      }
    } else if (monthlyNet > monthlyGross && monthlyGross === 0) {
      // If gross is 0 but net has value, use net as gross
      console.warn(`⚠️ [calculateAllIncomeTotals] Income "${income.name || 'unnamed'}" has Gross=0 but Net>0 - using Net as Gross`);
      const correctedGross = monthlyNet;
      monthlyNet = correctedGross * 0.77; // Estimate 23% tax
      return {
        gross: acc.gross + correctedGross,
        net: acc.net + monthlyNet,
        tax: acc.tax + (correctedGross - monthlyNet)
      };
    }

    // Calculate monthly tax as the difference
    const monthlyTax = monthlyGross - monthlyNet;

    // Debug log for development
    console.log(`📊 [calculateAllIncomeTotals] ${income.name || 'unnamed income'}:`, {
      type: income.type,
      category: income.category,
      grossAmount,
      monthlyGross,
      monthlyNet,
      monthlyTax,
      frequency,
      isValid: monthlyGross >= monthlyNet
    });

    return {
      gross: acc.gross + monthlyGross,
      net: acc.net + monthlyNet,
      tax: acc.tax + monthlyTax
    };
  }, { gross: 0, net: 0, tax: 0 });

  // Final validation: Ensure gross >= net
  if (totals.net > totals.gross) {
    console.error('❌ [calculateAllIncomeTotals] FINAL VALIDATION FAILED: Net > Gross in totals!', {
      gross: totals.gross,
      net: totals.net,
      difference: totals.net - totals.gross
    });
    
    // Correct by swapping
    const correctedGross = totals.net;
    const correctedNet = totals.gross;
    const correctedTax = correctedGross - correctedNet;
    
    console.warn('⚠️ [calculateAllIncomeTotals] Corrected totals by swapping:', {
      gross: correctedGross,
      net: correctedNet,
      tax: correctedTax
    });
    
    return {
      gross: Math.round(correctedGross * 100) / 100,
      net: Math.round(correctedNet * 100) / 100,
      tax: Math.round(correctedTax * 100) / 100
    };
  }

  // Round the totals to 2 decimal places for consistency
  return {
    gross: Math.round(totals.gross * 100) / 100,
    net: Math.round(totals.net * 100) / 100,
    tax: Math.round(totals.tax * 100) / 100
  };
}; 